import { BreadcrumbItems } from '@/components/custom-breadcrumb/custom-breadcrumb.types';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

// URL segments that have a title in `layout:breadcrumbs`. Anything else is a dynamic segment
// (an id) and is shown as it appears in the path.
const TITLED_SEGMENTS = [
  'agents',
  'new',
  'work-orders',
  'phone-numbers',
  'profile',
  'support',
  'surveys',
  'voices',
] as const;

type TitledSegment = (typeof TITLED_SEGMENTS)[number];

const isTitledSegment = (segment: string): segment is TitledSegment =>
  (TITLED_SEGMENTS as readonly string[]).includes(segment);

export const useBreadcrumbs = () => {
  const { t } = useTranslation(['layout', 'navigation']);
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  paths[0] = '/';
  let breadcrumbsItems: BreadcrumbItems = paths.map((path, index) => ({
    title:
      index === 0
        ? t('navigation:sections.dashboard.label')
        : isTitledSegment(path)
          ? t(`layout:breadcrumbs.${path}`)
          : path.split('-').join(' '),
    path: `${path}`,
  }));
  const clonedItems = [...breadcrumbsItems];
  breadcrumbsItems = breadcrumbsItems.map((item, index) => {
    if (index === 1) {
      const processedItem = { ...item, path: `/${item.path}` };
      clonedItems[index] = processedItem;
      return processedItem;
    }
    if (index > 1) {
      const processedItem = { ...item, path: `${clonedItems[index - 1].path}/${item.path}` };
      clonedItems[index] = processedItem;
      return processedItem;
    }
    return item;
  });

  if (breadcrumbsItems.length > 0) {
    breadcrumbsItems[breadcrumbsItems.length - 1].path = undefined;
  }

  return breadcrumbsItems;
};
